//     (c) 2012-2018 Airbnb, Inc.
//
//     polyglot.js may be freely distributed under the terms of the BSD
//     license. For all licensing information, details, and documention:
//     http://airbnb.github.com/polyglot.js
//
//
// Polyglot.js is an I18n helper library written in JavaScript, made to
// work both in the browser and in Node. It provides a simple solution for
// interpolation and pluralization, based off of Airbnb's
// experience adding I18n functionality to its Backbone.js and Node apps.
//
// Polylglot is agnostic to your translation backend. It doesn't perform any
// translation; it simply gives you a way to manage translated phrases from
// your client- or server-side JavaScript application.
//

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

const forEach = (target, callback, context) => {
    (Object.getOwnPropertyNames(target) || []).forEach((name) => {
        const value = target[name];
        callback.call(context, value, name);
    });
};

const warning = (display, message) => {
    console.warn(message);
};

const has = (target, prop) =>
    Object.prototype.hasOwnProperty.call(target, prop);

const trim = (target) => String.prototype.trim.call(target);

const warnDefault = (message) => {
    warning(false, message);
};

const replace = String.prototype.replace;
const split = String.prototype.split;

// #### Pluralization methods
// The string that separates the different phrase possibilities.
const delimiter = '||||';

const russianPluralGroups = (n) => {
    const lastTwo = n % 100;
    const end = lastTwo % 10;
    if (lastTwo !== 11 && end === 1) {
        return 0;
    }
    if (2 <= end && end <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) {
        return 1;
    }
    return 2;
};

// Mapping from pluralization group plural logic.
const pluralTypes = {
    arabic(n) {
        // http://www.arabeyes.org/Plural_Forms
        if (n < 3) { return n; }
        const lastTwo = n % 100;
        if (lastTwo >= 3 && lastTwo <= 10) return 3;
        return lastTwo >= 11 ? 4 : 5;
    },
    bosnian_serbian: russianPluralGroups,
    chinese() { return 0; },
    croatian: russianPluralGroups,
    french(n) { return n > 1 ? 1 : 0; },
    german(n) { return n !== 1 ? 1 : 0; },
    russian: russianPluralGroups,
    lithuanian(n) {
        if (n % 10 === 1 && n % 100 !== 11) { return 0; }
        return n % 10 >= 2 && n % 10 <= 9 && (n % 100 < 11 || n % 100 > 19) ? 1 : 2;
    },
    czech(n) {
        if (n === 1) { return 0; }
        return (n >= 2 && n <= 4) ? 1 : 2;
    },
    polish(n) {
        if (n === 1) { return 0; }
        const end = n % 10;
        return 2 <= end && end <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
    },
    icelandic(n) { return (n % 10 !== 1 || n % 100 === 11) ? 1 : 0; },
    slovenian(n) {
        const lastTwo = n % 100;
        if (lastTwo === 1) {
            return 0;
        }
        if (lastTwo === 2) {
            return 1;
        }
        if (lastTwo === 3 || lastTwo === 4) {
            return 2;
        }
        return 3;
    }
};


// Mapping from pluralization group to individual language codes/locales.
const pluralTypeToLanguages = {
    arabic: ['ar'],
    bosnian_serbian: ['bs-Latn-BA', 'bs-Cyrl-BA', 'srl-RS', 'sr-RS'],
    chinese: ['id', 'id-ID', 'ja', 'ko', 'ko-KR', 'lo', 'ms', 'th', 'th-TH', 'zh'],
    croatian: ['hr', 'hr-HR'],
    german: ['fa', 'da', 'de', 'en', 'es', 'fi', 'el', 'he', 'hi-IN', 'hu', 'hu-HU', 'it', 'nl', 'no', 'pt', 'sv', 'tr'],
    french: ['fr', 'tl', 'pt-br'],
    russian: ['ru', 'ru-RU'],
    lithuanian: ['lt'],
    czech: ['cs', 'cs-CZ', 'sk'],
    polish: ['pl'],
    icelandic: ['is'],
    slovenian: ['sl-SL']
};

function langToTypeMap(mapping) {
    const ret = {};
    forEach(mapping, (langs, type) => {
        forEach(langs, (lang) => {
            ret[lang] = type;
        });
    });
    return ret;
}

function pluralTypeName(locale) {
    const langToPluralType = langToTypeMap(pluralTypeToLanguages);
    return (
        langToPluralType[locale] ||
        langToPluralType[split.call(locale, /-/, 1)[0]] ||
        langToPluralType.en
    );
}

function pluralTypeIndex(locale, count) {
    return pluralTypes[pluralTypeName(locale)](count);
}

function escapeToken(token) {
    return token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function constructTokenRegex(opts) {
    const prefix = (opts && opts.prefix) || '%{';
    const suffix = (opts && opts.suffix) || '}';

    if (prefix === delimiter || suffix === delimiter) {
        throw new RangeError(`"${delimiter}" token is reserved for pluralization`);
    }

    return new RegExp(escapeToken(prefix) + '(.*?)' + escapeToken(suffix), 'g');
}

const dollarRegex = /\$/g;
const dollarBillsYall = '$$';
const defaultTokenRegex = /%\{(.*?)\}/g;

// ---------------------------------------------------------------------
// transformPhrase
// ---------------------------------------------------------------------

/**
 * transformPhrase(phrase, substitutions, locale, tokenRegex)
 *
 * Stejná logika jako v původní verzi – pluralizace a interpolace.
 */
function transformPhraseInternal(phrase, substitutions, locale, tokenRegex) {

    if (typeof phrase !== 'string') throw new TypeError('Polyglot.transformPhrase expects argument #1 to be string');

    if (substitutions == null) {
        return phrase;
    }

    let result = phrase;
    const interpolationRegex = tokenRegex || defaultTokenRegex;

    // allow number as a pluralization shortcut
    const options = typeof substitutions === 'number'
        ? { smart_count: substitutions }
        : substitutions;

    // Pluralizace
    if (options.smart_count != null && result) {
        const texts = split.call(result, delimiter);
        result = trim(
            texts[pluralTypeIndex(locale || this.defaultLocale, options.smart_count)] || texts[0]
        );
    }

    // Interpolace
    result = replace.call(result, interpolationRegex, (expression, argument) => {
        if (!has(options, argument) || options[argument] == null) { return expression; }
        // Escape $ kvůli replace tokenům
        return replace.call(String(options[argument]), dollarRegex, dollarBillsYall);
    });

    return result;
}

// veřejná bená verze pro přímý import (bez instance Polyglot)
export function transformPhrase(phrase, substitutions, locale) {
    return transformPhraseInternal(phrase, substitutions, locale);
}

// ---------------------------------------------------------------------
// Polyglot třída (ESM)
// ---------------------------------------------------------------------

export class Polyglot {

    constructor(options) {

        const opts = options || {};
        this.phrases = {};
        this.currentLocale = opts.locale || 'en';
        this.localeLoader = opts.loader || null;

        this.extend(opts.phrases || {}, this.defaultLocale);

        const allowMissing = opts.allowMissing ? transformPhraseInternal : null;
        this.onMissingKey = typeof opts.onMissingKey === 'function'
            ? opts.onMissingKey
            : allowMissing;

        this.warn = opts.warn || warnDefault;
        this.tokenRegex = constructTokenRegex(opts.interpolation);
    }

    getLoacale() {

        return this.currentLocale;

    }

    // polyglot.locale([locale])
    async setLocale(locale, forced = false) {

        if (locale && (forced || locale != this.currentLocale)) {
            if (this.localeLoader) {
                await this.load(locale, this.localeLoader);
            } else {
                this.currentLocale = locale;
            }
        }

        return this.currentLocale;

    }

    // polyglot.extend(phrases[, prefix])
    extend(morePhrases, prefix) {

        forEach(morePhrases, (phrase, key) => {
            const prefixedKey = prefix ? `${prefix}.${key}` : key;
            if (typeof phrase === 'object') {
                this.extend(phrase, prefixedKey);
            } else {
                this.phrases[prefixedKey] = phrase;
            }
        }, this);

    }

    // polyglot.unset(phrases)
    unset(morePhrases, prefix) {
        if (typeof morePhrases === 'string') {
            delete this.phrases[morePhrases];
        } else {
            forEach(morePhrases, (phrase, key) => {
                const prefixedKey = prefix ? `${prefix}.${key}` : key;
                if (typeof phrase === 'object') {
                    this.unset(phrase, prefixedKey);
                } else {
                    delete this.phrases[prefixedKey];
                }
            }, this);
        }
    }

    // polyglot.clear()
    clear() {
        this.phrases = {};
    }

    // polyglot.replace(phrases)
    replace(newPhrases) {
        this.clear();
        this.extend(newPhrases);
    }

    async load(locale, resourceLoader) {

        const resources = await resourceLoader(locale);

        if (!(resources && resources.length)) {
            console.warn(`Cannot change locale. Locale "${locale}" does not have any resources.`);
            return false;
        }

        this.clear();
        resources.forEach((phrases) => this.extend(phrases));
        this.currentLocale = locale;

        return true;

    }

    // polyglot.t(key, options)
    t(key, options) {

        let phrase;
        let result;
        const opts = options == null ? {} : options;

        if (typeof this.phrases[key] === 'string') {
            phrase = this.phrases[key];
        } else if (typeof opts._ === 'string') {
            phrase = opts._;
        } else if (this.onMissingKey) {
            const onMissingKey = this.onMissingKey;
            result = onMissingKey(key, opts, this.currentLocale, this.tokenRegex);
        } else {
            this.warn(`Missing translation for key: "${key}"`);
            result = key;
        }

        if (typeof phrase === 'string') {
            result = transformPhraseInternal(
                phrase,
                opts,
                this.currentLocale,
                this.tokenRegex
            );
        }

        return result;
    }

    // polyglot.has(key)
    has(key) {
        return has(this.phrases, key);
    }

    // statická utilita: Polyglot.transformPhrase(...)
    static transformPhrase(phrase, substitutions, locale) {
        return transformPhraseInternal(phrase, substitutions, locale);
    }
}

// default export pro pohodlný import
export default Polyglot;
